import { useEffect } from 'react';

import { Divider, Flex, Grid, GridItem } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { useHistory } from 'react-router-dom';
import { AcceptDenyButtonGroup } from 'screens/Levelup/LevelUpCF/components/AcceptDenyButtonGroup';
import { PraiseAdvice } from 'screens/Levelup/LevelUpCF/components/common';
import { openFeedbackSidePanel } from 'screens/Levelup/LevelUpCF/helpers';

import { Feedback, OpenSidePanel } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/newIllustrations/noFeedback.svg';

import { CONTINUOUS_FEEDBACK_PEER_STATUS } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextParseComponent } from 'uiToolkitV1/TextParseComponent';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { useSummaryPagination } from '../../hooks/useSummaryPagination';
import { ProfileSummaryCardTemplate } from '../ProfileSummaryCardTemplate';
import { useMySummaryFeedback } from './hooks';

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
`;

const ViewCommentButtonContainer = styled('div')`
  margin-left: auto;
  visibility: hidden;
  position: absolute;
  top: 40%;
  right: 0px;
  visibility: hidden;
  ${Container}:hover & {
    visibility: visible;
  }
`;

const ViewCommentButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  position: absolute;
  top: 40%;
  right: 0px;
  visibility: hidden;
  ${Container}:hover & {
    visibility: visible;
  }
`;

const StyledAcceptDenyButtonGroup = styled(AcceptDenyButtonGroup)`
  visibility: hidden;
  position: absolute;
  bottom: 20px;
  right: 0px;
  ${Container}:hover & {
    visibility: visible;
  }
`;

const ParsedTextContainer = styled('div')`
  visibility: visible;

  ${Container}:hover & {
    visibility: ${(props) => (props.$hideCommentOnHover ? 'hidden' : 'visible')};
  }
`;

const DateCompWrapper = styled.div`
  p {
    font-size: 16px;
    color: ${(props) => props.theme.colors.text.g2};
  }
`;

export const FeedbackRowForOneOnOneSidebar = ({
  requesterMiniProfile,
  requestTextDetails,
  timestamp,
  feedbackStatus,
  sentiment,
  continuousFeedbackId,
  postId,
  providerMiniProfile,
  postTextDetails,
  showDivider,
}) => {
  const history = useHistory();
  const isPendingType = feedbackStatus === 'PENDING';
  const isReceivedType = feedbackStatus === 'RECEIVED';
  const { displayName, profilePhoto } = isPendingType ? requesterMiniProfile : (providerMiniProfile ?? {});
  const { text, macros, peerStatus } = isPendingType ? requestTextDetails : (postTextDetails ?? {});
  const isNominated = peerStatus === CONTINUOUS_FEEDBACK_PEER_STATUS?.NOMINATED;
  const isAccepted = peerStatus === CONTINUOUS_FEEDBACK_PEER_STATUS.ACCEPTED;

  const onRowClickHandler = ({ feedbackId }) => openFeedbackSidePanel({ feedbackId, history });

  const onReceivedRowClickHandler = ({ feedbackId }) =>
    pushFeedbackParams({
      history,
      formType: 'SHOW_RECEIVED',
      typeMapped: true,
      feedbackId,
    });

  return (
    <>
      <Container
        onClick={
          isPendingType && isAccepted
            ? () => onRowClickHandler({ feedbackId: continuousFeedbackId })
            : isReceivedType
              ? () => onReceivedRowClickHandler({ feedbackId: postId })
              : () => {}
        }
        cursor={(isPendingType && isAccepted) || isReceivedType ? 'pointer' : 'default'}
        flexDir="column"
        position="relative"
      >
        <Flex alignItems="center">
          {/* <TextLabel color="text.g2" mb={'4px'}>
            {isPendingType ? 'Requested by' : 'Received From'}
          </TextLabel> */}
          <Flex alignItems="center">
            <Avatar size={24} src={profilePhoto} name={displayName} />
            <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
              {displayName}
            </TextBodyMedium>
          </Flex>
          <Flex ml="auto" alignItems="center">
            <TextBodyMedium color="text.g2">Date:&nbsp;</TextBodyMedium>
            <DateComp date={timestamp} textComponent={TextLabel} />
            {isReceivedType && (
              <>
                &nbsp;
                <TextBodyMedium color="text.g2">•</TextBodyMedium>&nbsp;
                <PraiseAdvice value={sentiment} />
              </>
            )}
          </Flex>
        </Flex>
        <Flex flexDir="column" mt="5px">
          <TextLabel color="text.g2" mb="4px">
            {isPendingType ? 'Request Details' : ''}
          </TextLabel>
          <>
            <ParsedTextContainer $hideCommentOnHover={false}>
              <TextParseComponent macros={macros} text={text} />
            </ParsedTextContainer>
            {/* <ViewCommentButtonContainer className="showOnHover">
              {isPendingType && isAccepted && (
                <ViewCommentButton variant="rowButtonOutlined" StartIcon={OpenSidePanel}>
                  View Request Details
                </ViewCommentButton>
              )}
              {isReceivedType && (
                <ViewCommentButton variant="rowButtonOutlined" StartIcon={OpenSidePanel}>
                  View Full Comment
                </ViewCommentButton>
              )}
            </ViewCommentButtonContainer> */}
            {isPendingType && isNominated && !isAccepted && (
              <StyledAcceptDenyButtonGroup feedbackId={continuousFeedbackId} />
            )}
          </>
        </Flex>
      </Container>
      {showDivider && <Divider color="neutral.CFD7DF" />}
    </>
  );
};

const FeedbackMySummaryRow = ({
  requesterMiniProfile,
  requestTextDetails,
  timestamp,
  feedbackStatus,
  sentiment,
  continuousFeedbackId,
  postId,
  providerMiniProfile,
  postTextDetails,
  showDivider,
}) => {
  const history = useHistory();
  const isPendingType = feedbackStatus === 'PENDING';
  const isReceivedType = feedbackStatus === 'RECEIVED';
  const { displayName, profilePhoto } = isPendingType ? requesterMiniProfile : (providerMiniProfile ?? {});
  const { text, macros, peerStatus } = isPendingType ? requestTextDetails : (postTextDetails ?? {});
  const isNominated = peerStatus === CONTINUOUS_FEEDBACK_PEER_STATUS?.NOMINATED;
  const isAccepted = peerStatus === CONTINUOUS_FEEDBACK_PEER_STATUS.ACCEPTED;

  const onRowClickHandler = ({ feedbackId }) => openFeedbackSidePanel({ feedbackId, history });

  const onReceivedRowClickHandler = ({ feedbackId }) =>
    pushFeedbackParams({
      history,
      formType: 'SHOW_RECEIVED',
      typeMapped: true,
      feedbackId,
    });

  return (
    <>
      <Container
        onClick={
          isPendingType && isAccepted
            ? () => onRowClickHandler({ feedbackId: continuousFeedbackId })
            : isReceivedType
              ? () => onReceivedRowClickHandler({ feedbackId: postId })
              : () => {}
        }
        cursor={(isPendingType && isAccepted) || isReceivedType ? 'pointer' : 'default'}
      >
        <Grid templateColumns="repeat(6, 1fr)" width="100%" gap={2}>
          <GridItem colSpan={2}>
            <Flex flexDir="column">
              <TextLabel color="text.g2" mb="4px">
                {isPendingType ? 'Requested by' : 'Received From'}
              </TextLabel>
              <Flex alignItems="center" sx={{ marginBottom: '8px' }}>
                <Avatar size={24} src={profilePhoto} name={displayName} />
                <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
                  {displayName}
                </TextBodyMedium>
              </Flex>
              <Flex mt="12px">
                <TextBodyMedium color="text.g2">Date:&nbsp;</TextBodyMedium>
                <DateCompWrapper>
                  <DateComp date={timestamp} />
                </DateCompWrapper>
                {isReceivedType && (
                  <>
                    &nbsp;
                    <TextBodyMedium color="text.g2">•</TextBodyMedium>&nbsp;
                    <PraiseAdvice value={sentiment} />
                  </>
                )}
              </Flex>
            </Flex>
          </GridItem>
          <GridItem colStart={3} colEnd={7} position="relative">
            <Flex flexDir="column">
              <TextLabel color="text.g2" mb="4px">
                {isPendingType ? 'Request Details' : 'Comment'}
              </TextLabel>
              <>
                <ParsedTextContainer $hideCommentOnHover>
                  <TextParseComponent macros={macros} text={text} />
                </ParsedTextContainer>
                <ViewCommentButtonContainer className="showOnHover">
                  {isPendingType && isAccepted && (
                    <ViewCommentButton variant="rowButtonOutlined" StartIcon={OpenSidePanel}>
                      View Request Details
                    </ViewCommentButton>
                  )}
                  {isReceivedType && (
                    <ViewCommentButton variant="rowButtonOutlined" StartIcon={OpenSidePanel}>
                      View Full Comment
                    </ViewCommentButton>
                  )}
                </ViewCommentButtonContainer>
                {isPendingType && isNominated && !isAccepted && (
                  <StyledAcceptDenyButtonGroup feedbackId={continuousFeedbackId} />
                )}
              </>
            </Flex>
          </GridItem>
        </Grid>
      </Container>
      {showDivider && <Divider color="neutral.CFD7DF" />}
    </>
  );
};

const FeedbackProfileSummaryCard = ({ employeeId }) => {
  const {
    user: { id },
  } = useAuthenticatedUser();
  const { hasMore, size, setDataLength, setPage } = useSummaryPagination();
  const { data, isLoading, isPreviousData, isFetching, isError } = useMySummaryFeedback({
    limit: size,
    offset: 0,
    employeeId: employeeId ?? id,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isLoading) {
      setDataLength(data?.length);
    }
  }, [isLoading, data]);

  if (isLoading) {
    return (
      <ProfileSummaryCardTemplate Icon={Feedback} heading="Feedback" showViewAllButton={false}>
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </ProfileSummaryCardTemplate>
    );
  }
  if (!data?.length) {
    return (
      <EmptySummaryCard
        Icon={Feedback}
        heading="Feedback"
        description="They haven't received any feedback yet"
        illustrationSrc={NoDataImg}
      />
    );
  }
  return (
    <ProfileSummaryCardTemplate
      Icon={Feedback}
      heading="Feedback"
      showViewAllButton={hasMore}
      onClickViewAll={() => {
        setPage((prev) => prev + 1);
      }}
      viewAllBtnDisabled={isPreviousData || !hasMore}
    >
      {data?.map(
        (
          {
            requesterMiniProfile,
            requestTextDetails,
            timestamp,
            feedbackStatus,
            sentiment,
            continuousFeedbackId,
            postId,
            providerMiniProfile,
            postTextDetails,
          },
          index
        ) => (
          <FeedbackMySummaryRow
            requesterMiniProfile={requesterMiniProfile}
            requestTextDetails={requestTextDetails}
            timestamp={timestamp}
            key={`${requesterMiniProfile?.displayName}-${index}-review-row`}
            feedbackStatus={feedbackStatus}
            sentiment={sentiment}
            continuousFeedbackId={continuousFeedbackId}
            postId={postId}
            providerMiniProfile={providerMiniProfile}
            postTextDetails={postTextDetails}
            showDivider={data?.data?.length - 1 !== index}
          />
        )
      )}
      {isFetching ? <SkeletonLoader /> : null}
    </ProfileSummaryCardTemplate>
  );
};

export { FeedbackProfileSummaryCard };
